<template>
	<v-app theme="light" style="background-color: #eeeeee">
		<VMain>
			<v-container fluid class="pt-6 pb-10">
				<v-row justify="center">
					<v-col cols="12" md="9" lg="7">

						<v-row v-if="loading" justify="center" class="py-16">
							<v-progress-circular indeterminate color="#0b7078" size="48" />
						</v-row>

						<v-alert v-else-if="loadError" type="error" class="mt-6">
							{{ loadError }}
						</v-alert>

						<v-card v-else class="success-card">
							<div class="success-header text-center px-8 py-8">
								<div class="success-check-wrap mx-auto mb-5">
									<v-icon size="32" color="#ffffff">fas fa-check</v-icon>
								</div>
								<h2 class="success-title mb-2">Audit submitted successfully</h2>
								<p class="success-subtitle mb-0">
									Your responses have been sent to {{ companyName }}.
								</p>
							</div>

							<v-card-text class="px-8 py-8">
								<span class="success-eyebrow">SUBMITTED BY</span>

								<div class="success-row d-flex align-center py-5">
									<div class="success-icon-box flex-shrink-0 mr-4">
										<v-icon size="18" color="#0b7078">fas fa-user</v-icon>
									</div>
									<div>
										<div class="success-label">Company name</div>
										<div class="success-value">{{ clientName }}</div>
									</div>
								</div>
								<v-divider />

								<div class="success-row d-flex align-center py-5">
									<div class="success-icon-box flex-shrink-0 mr-4">
										<v-icon size="18" color="#0b7078">fas fa-building</v-icon>
									</div>
									<div>
										<div class="success-label">Owner name</div>
										<div class="success-value">{{ companyName }}</div>
									</div>
								</div>
								<v-divider />

								<div class="success-row d-flex align-center py-5">
									<div class="success-icon-box flex-shrink-0 mr-4">
										<v-icon size="18" color="#0b7078">fas fa-shield-halved</v-icon>
									</div>
									<div>
										<div class="success-label mb-1">Account type</div>
										<span class="account-badge">
											<v-icon size="12" color="#a4621a" class="mr-1">fas fa-user</v-icon>
											Guest user
										</span>
									</div>
								</div>

								<div class="upsell-box mt-6 pa-7">
									<div class="d-flex align-center mb-3">
										<v-icon size="20" color="#0b7078" class="mr-2">far fa-star</v-icon>
										<span class="upsell-title">Unlock your full account</span>
									</div>
									<p class="upsell-text mb-6">
										As a guest, you can only submit forms. Register to view your scores, reports, and track sustainability progress.
									</p>
									<div class="d-flex flex-wrap upsell-actions">
										<v-btn
											depressed
											dark
											color="#0b7078"
											class="text-none mr-3 mb-2"
											style="border-radius:8px;"
											@click="registerNow"
										>
											<v-icon left size="16">fas fa-user-plus</v-icon>
											Register now
										</v-btn>
										<v-btn
											outlined
											color="#0b7078"
											class="text-none mb-2"
											style="border-radius:8px;"
											@click="maybeLater"
										>
											Maybe later
										</v-btn>
									</div>
								</div>
							</v-card-text>
						</v-card>

					</v-col>
				</v-row>
			</v-container>
		</VMain>
		<v-dialog v-model="registerConfirm" max-width="600px">
			<v-card class="confirm-card">
				<v-card-text class="text-center px-8 pt-10 pb-10">
					<div class="dialog-icon-wrap mx-auto mb-6">
						<v-icon size="26" color="#0b7078">fas fa-user-plus</v-icon>
					</div>

					<h2 class="mb-3">Create your account</h2>
					<p class="dialog-subtitle mb-8">
						You're currently browsing as a guest. Registering unlocks your scores, reports, and progress tracking — and this audit submission will be linked to your new account.
					</p>

					<div class="d-flex justify-center">
						<v-btn
							outlined
							color="error"
							class="px-8 mr-3"
							style="border-radius:8px;"
							@click="registerConfirm = false"
						>
							Cancel
						</v-btn>
						<v-btn
							depressed
							dark
							color="#0b7078"
							class="text-none px-8"
							@click="confirmRegister"
						>
							Yes, register me
						</v-btn>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-app>
</template>

<script>
import { mapGetters } from "vuex"
import RequestAPI from "../api/audit/request"
import LoadingState from "../consts/LoadingState"

export default {
	name: "GuestAuditSuccess",
	data() {
		return {
			loading: true,
			loadError: null,
			assignedForms: [],
			registerConfirm: false,
		}
	},
	computed: {
		...mapGetters("user", ["isLoggedIn", "getUser"]),
		clientName() {
			return this.getUser.clientName
		},
		userName() {
			return this.getUser.userName
		},
		latestItem() {
			return this.assignedForms.length > 0 ? this.assignedForms[0] : null
		},
		companyName() {
			return this.latestItem ? this.latestItem.senderName : ""
		},
		initials() {
			if (!this.userName) return ""
			return this.userName.trim().charAt(0).toUpperCase()
		},
	},
	mounted() {
		this.unsubscribeLoadingAction = this.$store.subscribeAction(action => {
			if (action.type === "loading/" + LoadingState.ACTION_END_LOADING) {
				this.loading = false
			}
			if (action.type === "loading/" + LoadingState.ACTION_SET_ERROR) {
				this.loading = false
				this.loadError = action.payload || "Something went wrong loading your submission."
			}
		})
		this.loadGuestRequest()
	},
	beforeDestroy() {
		if (this.unsubscribeLoadingAction) this.unsubscribeLoadingAction()
	},
	methods: {
		loadGuestRequest() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			RequestAPI.getGuestRequest()
				.then(result => {
					this.assignedForms = result.items.map(item => ({
						id: item.ID,
						title: item.TemplateName,
						senderName: item.ClientName,
						icon: null,
						template: item.Template,
						clientID: item.ClientID,
						recipient: item.Recipient,
						ownerName: item.OwnerName || null,
					}))

					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch(reason => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})
		},
		guestUpdate(){
			RequestAPI.updateGuest()
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully Register to the system!`,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		registerNow() {
			this.registerConfirm = true
		},
		maybeLater() {
			this.$router.push("/guestPortal")
		},
		confirmRegister() {
			this.registerConfirm = false
			RequestAPI.updateGuest()
				.then(() => {
					this.$router.push("/login")
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
	},
}
</script>

<style scoped>
.success-card {
	border-radius: 14px;
	overflow: hidden;
}
.success-header {
	background: #0b7078;
}
.success-check-wrap {
	width: 76px;
	height: 76px;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.16);
	display: flex;
	align-items: center;
	justify-content: center;
}
.success-title {
	color: #fff;
	line-height: 1.3;
}
.success-subtitle {
	color: #d8eeee;
}
.success-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: #0b7078;
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
}
.success-icon-box {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	background: #eef2f2;
	display: flex;
	align-items: center;
	justify-content: center;
}
.success-value {
	color: #1a1a1a;
	font-weight: 700;
	font-size: 16px;
}
.account-badge {
	display: inline-flex;
	align-items: center;
	background: #fcecd4;
	color: #a4621a;
	padding: 5px 12px;
	border-radius: 999px;
}
.upsell-box {
	background: #e3f5ee;
	border: 1px solid #c9ebdf;
	border-radius: 12px;
}
</style>